import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Bot, Check, Copy, Plus, Trash2 } from 'lucide-react';
import {
  getListServiceAccountApiKeysQueryKey,
  getListServiceAccountsQueryKey,
  useCreateServiceAccountApiKey,
  useListServiceAccountApiKeys,
  useListServiceAccounts,
  useRevokeServiceAccountApiKey,
  type ListServiceAccountApiKeysQueryResult,
  type ListServiceAccountsQueryResult,
} from '@reflex/ui/client/generated/service-accounts';
import { formatDateOnly } from '@reflex/ui/lib/format';
import { useCopyToClipboard } from '@reflex/ui/hooks/useCopyToClipboard';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { Label } from '@reflex/ui/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@reflex/ui/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@reflex/ui/components/ui/alert-dialog';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { NestedDetailHeader } from '@/components/shared/NestedDetailHeader';
import { DetailPageShell } from '@/components/shared/DetailPageShell';
import { RequireActiveOrganization } from '@/components/shared/RequireActiveOrganization';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import { useAuth } from '@/hooks/auth/useAuth';
import { useOrgRoutes } from '@/lib/useOrgRoutes';

type ServiceAccount = ListServiceAccountsQueryResult['serviceAccounts'][number];
type ServiceAccountApiKey = ListServiceAccountApiKeysQueryResult['keys'][number];

/**
 * Detail page for one service account. Rendered in place inside the
 * service-accounts list (and Organization hub) with a nested back link
 * (not the takeover BackChip — Esc / shell Back pop via ConfigureShell).
 */
export function ServiceAccountDetailPage({ embedded = false }: { embedded?: boolean }) {
  return (
    <RequireActiveOrganization title="Service account">
      {() => <ServiceAccountDetailLoader embedded={embedded} />}
    </RequireActiveOrganization>
  );
}

function ServiceAccountDetailLoader({ embedded = false }: { embedded?: boolean }) {
  const { id } = useParams<{ id: string }>();
  const accountId = id ?? '';
  const routes = useOrgRoutes();

  const accountsQuery = useListServiceAccounts();
  const account = accountsQuery.data?.serviceAccounts.find((a) => a.id === accountId);

  if (!accountId) {
    return (
      <PageLayout>
        <PageHeader title="Service account" description="Missing service account id." />
      </PageLayout>
    );
  }

  return (
    <DetailPageShell
      title="Service account"
      loadingDescription="Loading service account…"
      errorDescription="This service account could not be loaded."
      back={{
        to: routes.serviceAccounts,
        label: 'Service accounts',
        ariaLabel: 'Back to service accounts',
      }}
      isLoading={accountsQuery.isLoading}
      isError={accountsQuery.isError}
      data={account}
    >
      {(resolved) => (
        <ServiceAccountDetail key={resolved.id} account={resolved} embedded={embedded} />
      )}
    </DetailPageShell>
  );
}

function ServiceAccountDetail({
  account,
  embedded = false,
}: {
  account: ServiceAccount;
  embedded?: boolean;
}) {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const canWrite = can('service-accounts:write');

  const keysQueryKey = getListServiceAccountApiKeysQueryKey(account.id);
  const keysQuery = useListServiceAccountApiKeys(account.id);
  const keys = keysQuery.data?.keys ?? [];

  const [createOpen, setCreateOpen] = useState(false);
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<ServiceAccountApiKey | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: keysQueryKey });
    void queryClient.invalidateQueries({ queryKey: getListServiceAccountsQueryKey() });
  };

  const createMutation = useCreateServiceAccountApiKey({
    mutation: {
      onSuccess: (result) => {
        invalidate();
        toast.success('API key created');
        setCreateOpen(false);
        setCreatedToken(result.token);
      },
      onError: (err) =>
        toast.error('Failed to create API key', {
          description: err instanceof Error ? err.message : undefined,
        }),
    },
  });

  const revokeMutation = useRevokeServiceAccountApiKey({
    mutation: {
      onSuccess: () => {
        invalidate();
        toast.success('API key revoked');
      },
      onError: (err) =>
        toast.error('Failed to revoke API key', {
          description: err instanceof Error ? err.message : undefined,
        }),
    },
  });

  const isPending = createMutation.isPending || revokeMutation.isPending;

  const routes = useOrgRoutes();

  return (
    <PageLayout maxWidth="4xl" className={embedded ? 'p-0' : undefined}>
      <NestedDetailHeader
        back={{
          to: routes.serviceAccounts,
          label: 'Service accounts',
          ariaLabel: 'Back to service accounts',
        }}
        title={account.name}
        description="Machine identity for this organization. Mint API keys below to call the Reflex API with this account's role."
        icon={<Bot className="h-5 w-5 text-muted-foreground" />}
        iconVariant="box"
      />

      <SectionCard title="Role" description="Permissions this account acts with.">
        <div className="flex flex-wrap gap-2">
          {account.roles.length > 0 ? (
            account.roles.map((role) => (
              <Badge key={role.id} variant="secondary">
                {role.name}
              </Badge>
            ))
          ) : (
            <span className="text-sm text-muted-foreground">No role assigned.</span>
          )}
        </div>
      </SectionCard>

      <SectionCard
        title="API keys"
        description="Each token is shown once at creation. After that, only the token prefix is visible here."
        action={
          canWrite ? (
            <Button size="sm" onClick={() => setCreateOpen(true)} disabled={isPending}>
              <Plus className="icon-sm" /> Create API key
            </Button>
          ) : undefined
        }
        data-testid="service-account-keys"
      >
        <AsyncContent
          isLoading={keysQuery.isLoading}
          isError={keysQuery.isError}
          loadingMessage="Loading API keys…"
          errorMessage="Failed to load API keys."
        >
          {keys.length > 0 ? (
            <ul className="divide-y divide-border rounded-md border">
              {keys.map((key) => (
                <li
                  key={key.id}
                  className="flex items-center justify-between gap-3 p-4"
                  data-testid={`service-account-key-row-${key.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <span className="text-sm font-medium">{key.name}</span>
                    <span className="ml-2 font-mono text-xs text-muted-foreground">
                      {key.tokenPrefix}...
                    </span>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      Created {formatDateOnly(key.createdAt)} · Last used{' '}
                      {key.lastUsedAt === null ? 'Never' : formatDateOnly(key.lastUsedAt)}
                    </div>
                  </div>
                  {canWrite && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                      onClick={() => setRevoking(key)}
                      disabled={isPending}
                      aria-label={`Revoke ${key.name}`}
                    >
                      <Trash2 className="icon-sm" /> Revoke
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              variant="bordered"
              title="No API keys yet. Create one to authenticate as this service account."
            />
          )}
        </AsyncContent>
      </SectionCard>

      <CreateKeyDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        isPending={createMutation.isPending}
        onSubmit={(name) => createMutation.mutate({ id: account.id, data: { name } })}
      />

      <TokenRevealDialog token={createdToken} onClose={() => setCreatedToken(null)} />

      <AlertDialog
        open={revoking !== null}
        onOpenChange={(next) => {
          if (!next) setRevoking(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this API key?</AlertDialogTitle>
            <AlertDialogDescription>
              {revoking?.name} will be permanently revoked. Any automation using this key loses
              access immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (revoking) revokeMutation.mutate({ id: account.id, keyId: revoking.id });
                setRevoking(null);
              }}
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageLayout>
  );
}

interface CreateKeyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isPending: boolean;
  onSubmit: (name: string) => void;
}

function CreateKeyDialog({ open, onOpenChange, isPending, onSubmit }: CreateKeyDialogProps) {
  const [name, setName] = useState('');

  function handleOpenChange(next: boolean) {
    if (!next) setName('');
    onOpenChange(next);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || isPending) return;
    onSubmit(trimmed);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create API key</DialogTitle>
            <DialogDescription>
              Name the key after where you will use it, like a CI job or a deploy script.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-2">
            <Label htmlFor="service-account-key-name">Name</Label>
            <Input
              id="service-account-key-name"
              placeholder="e.g. github-actions"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="off"
              maxLength={100}
            />
          </div>
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || isPending}>
              {isPending ? 'Creating…' : 'Create key'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface TokenRevealDialogProps {
  token: string | null;
  onClose: () => void;
}

/** One-time token reveal: the full token is only ever shown in this dialog. */
function TokenRevealDialog({ token, onClose }: TokenRevealDialogProps) {
  const { copied, copy } = useCopyToClipboard();

  return (
    <Dialog
      open={token !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>API key created</DialogTitle>
          <DialogDescription>
            Copy the token now and store it somewhere safe. You will not be able to see this token
            again.
          </DialogDescription>
        </DialogHeader>
        <code
          className="mt-2 block select-all break-all rounded-md border bg-muted/60 p-3 font-mono text-sm"
          data-testid="service-account-key-token"
        >
          {token}
        </code>
        <DialogFooter className="mt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              if (token) copy(token);
            }}
            aria-label={copied ? 'Copied token' : 'Copy token'}
          >
            {copied ? (
              <>
                <Check className="icon-sm text-status-success" /> Copied
              </>
            ) : (
              <>
                <Copy className="icon-sm" /> Copy token
              </>
            )}
          </Button>
          <Button type="button" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
